import React, { useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { TankState } from '@tankworld/shared';
import { BattlefieldTerrain } from '@tankworld/simulation';
import { CameraMode } from '../../state/game-store.js';

interface FollowCameraProps {
  tank: TankState | undefined;
  cameraMode: CameraMode;
  terrain: BattlefieldTerrain;
}

export const FollowCamera: React.FC<FollowCameraProps> = ({ tank, cameraMode, terrain }) => {
  const { camera } = useThree();
  const currentLookAt = useRef(new THREE.Vector3());
  const currentCamPos = useRef(new THREE.Vector3(0, 5, -12));

  useFrame((_, delta) => {
    if (!tank) return;

    const turretYaw = tank.turretYaw;
    const gunPitch = tank.gunPitch;
    const tankPos = new THREE.Vector3(tank.position.x, tank.position.y, tank.position.z);

    if (cameraMode === 'sniper') {
      // Sniper gun optic: high zoom, looking directly along barrel bore
      (camera as THREE.PerspectiveCamera).fov = 18;
      camera.updateProjectionMatrix();

      const barrelElevation = gunPitch;
      const fwd = new THREE.Vector3(
        Math.sin(turretYaw) * Math.cos(barrelElevation),
        Math.sin(barrelElevation),
        Math.cos(turretYaw) * Math.cos(barrelElevation)
      );

      // Position optic at mantlet
      const opticPos = tankPos.clone().add(new THREE.Vector3(0, 1.8, 0)).addScaledVector(fwd, 1.8);
      camera.position.copy(opticPos);

      const lookTarget = opticPos.clone().addScaledVector(fwd, 100);
      camera.lookAt(lookTarget);
    } else if (cameraMode === 'commander') {
      // Commander's cupola: wide FOV first-person view
      (camera as THREE.PerspectiveCamera).fov = 70;
      camera.updateProjectionMatrix();

      const fwd = new THREE.Vector3(Math.sin(turretYaw), 0, Math.cos(turretYaw));
      const cupolaPos = tankPos.clone().add(new THREE.Vector3(0, 2.3, 0));
      camera.position.copy(cupolaPos);

      const lookTarget = cupolaPos.clone().addScaledVector(fwd, 50);
      lookTarget.y += Math.sin(gunPitch) * 50;
      camera.lookAt(lookTarget);
    } else {
      // Third-person chase camera
      (camera as THREE.PerspectiveCamera).fov = 55;
      camera.updateProjectionMatrix();

      const distance = 12.0;
      const height = 4.5;

      // Position camera behind turret aim vector for intuitive aiming
      const camYaw = turretYaw + Math.PI; // Look from behind
      const targetX = tankPos.x + Math.sin(camYaw) * distance;
      const targetZ = tankPos.z + Math.cos(camYaw) * distance;

      // Ensure camera does not clip through ground terrain
      const groundH = terrain.getHeight(targetX, targetZ);
      const targetY = Math.max(groundH + 1.5, tankPos.y + height - Math.sin(gunPitch) * 4.0);

      const desiredPos = new THREE.Vector3(targetX, targetY, targetZ);
      currentCamPos.current.lerp(desiredPos, delta * 10.0);
      camera.position.copy(currentCamPos.current);

      const desiredLook = tankPos.clone().add(new THREE.Vector3(0, 1.8, 0));
      currentLookAt.current.lerp(desiredLook, delta * 15.0);
      camera.lookAt(currentLookAt.current);
    }
  });

  return null;
};
